import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CurrencyResponseDTO {
  id: number;
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
  isBaseCurrency: boolean;
  decimalPlaces: number;
  displayOrder: number;
  legacyEnumValue?: number;
  latestExchangeRate?: number;
  lastRateUpdate?: Date;
}

export interface CurrencyRequestDTO {
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
  isBaseCurrency: boolean;
  decimalPlaces: number;
  displayOrder: number;
}

export interface Result<T> {
  data: T;
  isSuccess: boolean;
  message: string;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private apiUrl = `${environment.apiUrl}/currency`;

  constructor(private http: HttpClient) { }

  /**
   * Obtiene todas las monedas activas
   */
  getActiveCurrencies(): Observable<Result<CurrencyResponseDTO[]>> {
    return this.http.get<Result<CurrencyResponseDTO[]>>(`${this.apiUrl}/active`);
  }

  /**
   * Obtiene monedas disponibles para proyectos internacionales
   */
  getInternationalCurrencies(): Observable<Result<CurrencyResponseDTO[]>> {
    return this.http.get<Result<CurrencyResponseDTO[]>>(`${this.apiUrl}/international`);
  }

  /**
   * Obtiene la moneda base del sistema (COP)
   */
  getBaseCurrency(): Observable<Result<CurrencyResponseDTO>> {
    return this.http.get<Result<CurrencyResponseDTO>>(`${this.apiUrl}/base`);
  }

  /**
   * Obtiene todas las monedas del sistema
   */
  getAllCurrencies(): Observable<Result<CurrencyResponseDTO[]>> {
    return this.http.get<Result<CurrencyResponseDTO[]>>(this.apiUrl);
  }

  /**
   * Obtiene una moneda por su ID
   */
  getCurrencyById(id: number): Observable<Result<CurrencyResponseDTO>> {
    return this.http.get<Result<CurrencyResponseDTO>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Obtiene una moneda por su código
   */
  getCurrencyByCode(code: string): Observable<Result<CurrencyResponseDTO>> {
    return this.http.get<Result<CurrencyResponseDTO>>(`${this.apiUrl}/code/${code}`);
  }

  /**
   * Crea una nueva moneda
   */
  createCurrency(currency: CurrencyRequestDTO): Observable<Result<CurrencyResponseDTO>> {
    return this.http.post<Result<CurrencyResponseDTO>>(this.apiUrl, currency);
  }

  /**
   * Actualiza una moneda existente
   */
  updateCurrency(id: number, currency: CurrencyRequestDTO): Observable<Result<CurrencyResponseDTO>> {
    return this.http.put<Result<CurrencyResponseDTO>>(`${this.apiUrl}/${id}`, currency);
  }

  /**
   * Activa o desactiva una moneda
   */
  setCurrencyStatus(id: number, isActive: boolean): Observable<Result<boolean>> {
    return this.http.patch<Result<boolean>>(`${this.apiUrl}/${id}/status`, isActive);
  }

  /**
   * Formatea un monto con la moneda especificada
   */
  formatAmount(amount: number, currency: CurrencyResponseDTO): string {
    const formatter = new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: currency.decimalPlaces,
      maximumFractionDigits: currency.decimalPlaces
    });

    // Para COP, usar el símbolo personalizado
    if (currency.code === 'COP') {
      return `$${amount.toLocaleString('es-CO', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      })}`;
    }

    return formatter.format(amount);
  }

  /**
   * Obtiene el símbolo de la moneda para mostrar en la UI
   */
  getCurrencySymbol(currency: CurrencyResponseDTO): string {
    return currency.symbol || currency.code;
  }
}
